import React, { useState } from "react";
import PropTypes from "prop-types";
import { filter } from "lodash";
// @mui
import {
  Card,
  Table,
  Stack,
  Paper,
  Popover,
  TableRow,
  MenuItem,
  TableBody,
  TableCell,
  Typography,
  IconButton,
  TableContainer,
  TablePagination,
  Button,
} from "@mui/material";
import Scrollbar from "../../../components/scrollbar/Scrollbar";
import Label from "../../../components/label";
import Iconify from "../../../components/iconify";
import { showRoles } from "../../../utils/showRoles";
import { useDispatch, useSelector } from "react-redux";
import { setDialog } from "../../../features/dialog/dialogSlice";
import TenantTableToolbar from "./TenantTableToolbar";
import TenantTableHead from "./TenantTableHead";
import {
  getCurrentOfficesCount,
  getCurrentUserCount,
} from "../../../utils/tenantUtils";
import { useChangeTenantMutation } from "../../../features/users/usersApiSlice";
import { selectCurrentUserData } from "../../../features/auth/authSlice";

const TABLE_HEAD = [
  { id: "tenantName", label: "Hauptpraxis", align: false },
  { id: "tenantSelected", label: "", align: false },
  { id: "contactPerson", label: "Kontaktperson", align: false },
  { id: "userCount", label: "Anzahl Benutzer", align: false },
  { id: "officeCount", label: "Anzahl Praxen", align: false },
  { id: "createdAd", label: "Erstellt am", align: false },
];

// Utility functions
function descendingComparator(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function getComparator(order, orderBy) {
  return order === "desc"
    ? (a, b) => descendingComparator(a, b, orderBy)
    : (a, b) => -descendingComparator(a, b, orderBy);
}

function applySortFilter(array, comparator, query) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = comparator(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });

  if (query) {
    return filter(
      array,
      (tenant) =>
        tenant?.tenantName.toLowerCase().indexOf(query.toLowerCase()) !== -1
    );
  }

  return stabilizedThis.map((el) => el[0]);
}

function TenantTable({ tenants }) {
  const dispatch = useDispatch();
  const currentUserData = useSelector(selectCurrentUserData);

  const [changeTenant] = useChangeTenantMutation();
  const [open, setOpen] = useState(null);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState("asc");
  const [selected, setSelected] = useState([]);
  const [user, setUser] = useState(null);
  const [orderBy, setOrderBy] = useState("name");
  const [filterName, setFilterName] = useState("");
  const [rowsPerPage, setRowsPerPage] = useState(10);

  console.log("currentUserData :>> ", currentUserData);

  const handleSelectTenant = async (tenant) => {
    try {
      let result = await changeTenant({ tenantId: tenant._id }).unwrap();
      if (result.type === "success") {
        dispatch(
          setDialog({
            title: "Tenant geändert!",
            type: "SUCCESS",
            data: {
              successText: result?.message ?? "",
            },
          })
        );
      }
    } catch (error) {
      console.log("error :>> ", error);
      window.alert("Probleme beim Ändern des Tenants");
    } finally {
      window.location.reload();
    }
  };

  const handleEditUser = () => {
    dispatch(
      setDialog({
        title: "Benutzer bearbeiten",
        type: "EDIT",
        content: "create-user-form",
        data: user,
      })
    );
    handleCloseMenu();
  };

  const handleChangePassword = () => {
    dispatch(
      setDialog({
        type: "password-change",
        user: user,
      })
    );
    handleCloseMenu();
  };

  const handleDeleteUser = () => {
    dispatch(
      setDialog({
        content: "delete-user",
        data: user,
      })
    );
    handleCloseMenu();
  };

  const handleOpenMenu = (event, user) => {
    setUser(user);
    setOpen(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setUser(null);
    setOpen(null);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = tenants.map((n) => n.tenantName);
      setSelected(newSelecteds);
      return;
    }
    setSelected([]);
  };

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name);
    let newSelected = [];
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1));
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      );
    }
    setSelected(newSelected);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    setPage(0);
    setFilterName(event.target.value);
  };

  const filteredUsers = applySortFilter(
    tenants,
    getComparator(order, orderBy),
    filterName
  );

  const isNotFound = !filteredUsers.length && !!filterName;

  return (
    <Card>
      <TenantTableToolbar
        numSelected={selected.length}
        filterName={filterName}
        onFilterName={handleFilterByName}
      />

      <Scrollbar>
        <TableContainer sx={{ minWidth: 800 }}>
          <Table>
            <TenantTableHead
              order={order}
              orderBy={orderBy}
              headLabel={TABLE_HEAD}
              rowCount={tenants.length}
              numSelected={selected.length}
              onRequestSort={handleRequestSort}
              onSelectAllClick={handleSelectAllClick}
            />
            <TableBody>
              {filteredUsers
                .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                .map((row) => {
                  const { _id, tenantName, contactPerson } = row;
                  const selectedUser = selected.indexOf(tenantName) !== -1;
                  return (
                    <TableRow
                      hover
                      key={_id}
                      tabIndex={-1}
                      role="checkbox"
                      selected={selectedUser}
                    >
                      <TableCell padding="checkbox">
                        <></>
                      </TableCell>
                      <TableCell component="th" scope="row" padding="none">
                        <Stack direction="row" alignItems="center" spacing={2}>
                          <Typography variant="subtitle2" noWrap>
                            {tenantName}
                          </Typography>
                        </Stack>
                      </TableCell>
                      <TableCell>
                        {row._id === currentUserData.tenant && (
                          <Label color={"warning"}>AUSGEWÄHLT</Label>
                        )}
                      </TableCell>

                      <TableCell align="left">{contactPerson}</TableCell>

                      <TableCell align="left">
                        {getCurrentUserCount(row)}
                      </TableCell>
                      <TableCell align="left">
                        {getCurrentOfficesCount(row)}
                      </TableCell>

                      <TableCell>
                        {new Date(row?.createdAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          onClick={() => handleSelectTenant(row)}
                        >
                          Tenant auswählen
                        </Button>
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="large"
                          color="inherit"
                          disabled
                          onClick={(e) => handleOpenMenu(e, row)}
                        >
                          <Iconify icon={"eva:more-vertical-fill"} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
              {filteredUsers.length === 0 && (
                <TableRow
                  style={{ height: 53 * (rowsPerPage - tenants.length) }}
                >
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>

            {isNotFound && (
              <TableBody>
                <TableRow>
                  <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                    <Paper
                      sx={{
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="h6" paragraph>
                        Nicht gefunden
                      </Typography>

                      <Typography variant="body2">
                        Keine Ergebnisse gefunden für &nbsp;
                        <strong>&quot;{filterName}&quot;</strong>.
                        <br /> Versuche es mit einem anderen Suchbegriff oder
                        überprüfe die Schreibweise.
                      </Typography>
                    </Paper>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>
      </Scrollbar>

      <TablePagination
        rowsPerPageOptions={[5, 10, 25]}
        component="div"
        count={tenants.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
      <Popover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleCloseMenu}
        anchorOrigin={{ vertical: "top", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            p: 1,
            width: 180,
            "& .MuiMenuItem-root": {
              px: 1,
              typography: "body2",
              borderRadius: 0.75,
            },
          },
        }}
      >
        <MenuItem onClick={() => handleEditUser()}>
          <Iconify icon={"eva:edit-fill"} sx={{ mr: 2 }} />
          Bearbeiten
        </MenuItem>

        <MenuItem onClick={() => handleChangePassword()}>
          <Iconify icon={"carbon:password"} sx={{ mr: 2 }} />
          Passwort ändern
        </MenuItem>

        <MenuItem
          onClick={() => handleDeleteUser()}
          sx={{ color: "error.main" }}
        >
          <Iconify icon={"eva:trash-2-outline"} sx={{ mr: 2 }} />
          Löschen
        </MenuItem>
      </Popover>
    </Card>
  );
}

TenantTable.propTypes = {
  tenants: PropTypes.array,
  userCount: PropTypes.string,
};

export default TenantTable;
